// src/utils/contactInfoClient.js
// Lightweight CLIENT-side contact-info detector — a soft, as-you-type pre-warning
// that mirrors the server's real gate (contentModeration.detectContactInfo). It
// only needs to catch the common phone/email/URL cases; the server stays
// authoritative and bypass-resistant. Returns { found, kind } or null.
//
// kind ∈ 'phone' | 'email' | 'url'.
//
// opts.eager (default false): trip EARLIER, while the user is still typing, for
// input-time warnings — phone at 7+ consecutive digits (vs a full 10), and email
// as soon as local@domain appears (before the .TLD is finished). Strict mode
// mirrors the server; eager mode favors a faster nudge and accepts a few more
// false positives, which is harmless for a soft warning — the server still
// decides what actually gets blocked.
//
// opts.flagOwnDomainUrl (default false): also flag our own ehandshake.com URLs.
// In a caption, an ehandshake.com landing-page link is the INTENDED flow, so it's
// excluded. In a headline (burned onto the video as plain text), no URL belongs —
// not even our own — so the headline editor passes this true.
//
// NOTE: PublishPreviewModal still has a near-identical local helper that predates
// this util (detectCaptionContactInfo) — consolidate onto this when convenient.

export function detectContactInfoClient(text, { eager = false, flagOwnDomainUrl = false } = {}) {
  if (!text) return null;
  const t = String(text);

  // Email — standard + lightly obfuscated "name at domain dot com".
  // Eager also trips on local@domain before the TLD (e.g. "brett@gmail").
  if (
    /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i.test(t) ||
    /\b[a-z0-9._%+-]+\s+at\s+[a-z0-9.-]+\s+dot\s+[a-z]{2,}\b/i.test(t) ||
    (eager && /[^\s@]+@[^\s@]+/.test(t))
  ) {
    return { found: true, kind: 'email' };
  }

  // Phone — realistic formats + a bare 10–15 digit run (matches the server's
  // bare-digit check; catches "0805327702" with no separators).
  // Eager also trips on any run of 7+ consecutive digits (before the 10th).
  if (
    /\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}/.test(t) || // US/CA 10-digit grouped
    /\+\d[\d\s().\-]{7,}\d/.test(t) ||                  // international +CC
    /\b\d{10,15}\b/.test(t) ||                          // bare run
    (eager && /\d{7,}/.test(t))
  ) {
    return { found: true, kind: 'phone' };
  }

  // URL — explicit scheme/www or a bare domain.tld. Our own domain is excluded
  // unless flagOwnDomainUrl is set (headline context). No eager variant — a URL
  // isn't recognizable until it has a TLD anyway.
  const urlRe = /\b(?:https?:\/\/|www\.)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|us|biz|info|app|shop|store|site|online|link|bio|me|tv)\b(?:\/[^\s]*)?/ig;
  let m;
  while ((m = urlRe.exec(t)) !== null) {
    const host = m[0].replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].toLowerCase();
    const isOwn = host === 'ehandshake.com' || host.endsWith('.ehandshake.com');
    if (flagOwnDomainUrl || !isOwn) {
      return { found: true, kind: 'url' };
    }
  }

  return null;
}
